import axios from "axios";
import { toast } from "material-react-toastify";
import { sanitizePhoneDigitsOnly, toLowercaseName } from "../../../Utils";
import { DEFAULT_PHONE_NUMBER } from "../../../Utils/CONST";

const COMMON_URL = `${process.env.REACT_APP_SPRING_API_URL}`;
const BG_URL = `${process.env.REACT_APP_BG_API_URL}`;
const GETHOTELIMAGESURL = "user/groupBranding";
const SETHOTELIMAGESURL = "common/set-group-whitelabel-config";
const USERID = "core/api/v1/image/update-user-id-images";
const GUESTSID = "core/api/v1/image/update-guests-id-images";
const ISHAPRIMARYDOC = "core/api/v1/image/upload-isha-primary-attachments";
const ISHAOTHERDOC = "core/api/v1/image/upload-isha-other-guests-attachments";
const GETISHADOCS = "core/api/v1/image/get-isha-guest-documents";
const GETUSERID = "core/api/v1/image/get-user-id-images";
const UPDATEHOTELLOGO = "core/api/v1/image/update-hotel-logo";

// About 30 components (header, sidebars, calendars, reports, modals) fetch the
// group branding when they mount, so every page change re-fetched it several
// times. Branding only changes when it is saved: a save here clears this, and a
// save elsewhere (another session, Meta) shows up within BRANDING_TTL_MS.
const BRANDING_TTL_MS = 5 * 60 * 1000;
let brandingCache = null; // { hostUrl, at, promise }

export const clearGroupBrandingCache = () => {
  brandingCache = null;
};

const fetchGroupBranding = (hostUrl) => {
  const now = Date.now();
  if (
    brandingCache &&
    brandingCache.hostUrl === hostUrl &&
    now - brandingCache.at < BRANDING_TTL_MS
  ) {
    return brandingCache.promise;
  }
  const promise = axios
    .get(COMMON_URL + GETHOTELIMAGESURL + "?hostUrl=" + `${hostUrl}`)
    .then((resp) => resp.data);
  const entry = { hostUrl, at: now, promise };
  brandingCache = entry;
  // A failed fetch is not kept: the next caller tries again, as before.
  promise.catch(() => {
    if (brandingCache === entry) brandingCache = null;
  });
  return promise;
};

export const getAllImagesAndUrl = async () => {
  return (
    fetchGroupBranding(window.location.hostname)
      // Each caller gets its own copy, so one caller changing it can't change another's.
      .then((data) => (data && typeof data === "object" ? { ...data } : data))
      .catch((err) => {
        throw new Error(err);
      })
  );
};

export const setLogoAndFavicon = async (data) => {
  return axios
    .post(COMMON_URL + SETHOTELIMAGESURL, data)
    .then((resp) => {
      clearGroupBrandingCache();
      return resp.data;
    })
    .catch((err) => {
      throw new Error(err);
    });
};

export const setBookingUserIdImages = async (data) => {
  const baseName = toLowercaseName(
    data?.name || data?.guestName || data?.userName || ""
  );
  const fallbackFromEmail = data?.emailId
    ? toLowercaseName(String(data.emailId).split("@")[0])
    : "";
  const payload = {
    ...data,
    name: baseName || fallbackFromEmail,
    phone: sanitizePhoneDigitsOnly(
      data?.phone || data?.guestPhone || data?.contact || DEFAULT_PHONE_NUMBER
    ),
  };
  return axios
    .post(BG_URL + USERID, payload)
    .then((resp) => resp.data)
    .catch((err) => {
      toast.error(err.response.data);
    });
};

export const setOtherGuestImages = async (data) => {
  const payload = {
    ...data,
    otherGuestIdProofs: (data?.otherGuestIdProofs || []).map((p) => {
      const derivedName = toLowercaseName(
        p?.name ||
          p?.guestName ||
          (p?.emailId ? String(p.emailId).split("@")[0] : "")
      );
      const derivedPhone = sanitizePhoneDigitsOnly(
        p?.phone || p?.guestPhone || p?.contact || ""
      );
      return {
        ...p,
        name: derivedName,
        phone: derivedPhone,
      };
    }),
  };
  return axios
    .post(COMMON_URL + GUESTSID, payload)
    .then((resp) => resp.data)
    .catch((err) => {
      console.log(err);
    });
};

export const setIshaGuestDocument = async (data) => {
  return axios
    .post(COMMON_URL + ISHAPRIMARYDOC, data)
    .then((resp) => resp.data)
    .catch((err) => {
      console.log(err);
    });
};

export const setIshaOtherGuestDocuments = async (data) => {
  return axios
    .post(COMMON_URL + ISHAOTHERDOC, data)
    .then((resp) => resp.data)
    .catch((err) => {
      console.log(err);
    });
};

export const getIshaDocuments = async (emailId) => {
  return axios
    .get(COMMON_URL + GETISHADOCS + "?emailId=" + emailId)
    .then((resp) => resp.data)
    .catch((err) => {
      console.log(err);
    });
};
export const setIshaUserImages = async (data) => {
  return axios
    .post(
      "https://isha-api.stayflexi.com/web-api/isha/update-user-ID-images",
      data
    )
    .then((resp) => resp.data)
    .catch((err) => {
      throw new Error(err);
    });
};

export const getUserId = async (emailId, opts = {}) => {
  const { name, phone, base64Format } = opts || {};
  const params = new URLSearchParams();
  if (emailId) params.append("emailId", emailId);
  const baseName = toLowercaseName(
    name || (emailId ? String(emailId).split("@")[0] : "")
  );
  const basePhone = sanitizePhoneDigitsOnly(phone || DEFAULT_PHONE_NUMBER);
  if (baseName) params.append("name", baseName);
  if (basePhone) params.append("phone", basePhone);
  if (base64Format) {
    params.append("base64Format", "true");
  }

  return axios
    .get(BG_URL + GETUSERID + "?" + params.toString())
    .then((resp) => resp.data)
    .catch((err) => {
      throw new Error(err);
    });
};
export const getUserIdWithHotelId = async (emailId, hotelId, opts = {}) => {
  const { name, phone } = opts || {};
  const params = new URLSearchParams();
  if (emailId) params.append("emailId", emailId);
  if (hotelId) {
    params.append("hotelId", hotelId);
    params.append("hotel_id", hotelId);
  }
  const baseName = toLowercaseName(
    name || (emailId ? String(emailId).split("@")[0] : "")
  );
  const basePhone = sanitizePhoneDigitsOnly(phone || "");
  if (baseName) params.append("name", baseName);
  if (basePhone) params.append("phone", basePhone);

  return axios
    .get(COMMON_URL + GETUSERID + "?" + params.toString())
    .then((resp) => resp.data)
    .catch((err) => {
      throw new Error(err);
    });
};

export const getUserIdIsha = async (emailId, hotelId) => {
  return axios
    .get(
      COMMON_URL +
        "web-api/isha/get-user-id-images" +
        "?emailId=" +
        emailId +
        "&hotelId=" +
        hotelId
    )
    .then((resp) => resp.data)
    .catch((err) => {
      throw new Error(err);
    });
};

export const setHotelLogo = async (data) => {
  return axios
    .post(BG_URL + UPDATEHOTELLOGO, data)
    .then((resp) => resp.data)
    .then(() => toast.success("Hotel Logo updated successfully"))
    .catch((err) => {
      throw new Error(err);
    });
};

const sanitizePhoneNumberForIdUpload = (phone) => {
  if (!phone || phone.trim() === '') {
    return '0000000000';
  }
  // Remove all non-digit characters (special symbols, whitespace, etc.)
  const sanitized = phone.replace(/\D/g, '');
  if (sanitized === '') {
    return '0000000000';
  }
  return sanitized;
};

export const moveUserIdImages = async (hotelId, oldName, oldPhone, oldEmail, newName, newPhone, newEmail) => {
  if (oldName === newName && oldPhone === newPhone && oldEmail === newEmail) {
    return Promise.resolve();
  }

  try {
    // Get user id images data using the existing getUserId function
    const userImages = await getUserId(oldEmail, { name: oldName, phone: oldPhone, base64Format: true });

    // Upload to new location if images exist
    if (userImages?.frontUrl || userImages?.backUrl || userImages?.passportImageUrl) {
      console.log('Moving user ID images from old to new customer details');

      // Upload to new location using existing setBookingUserIdImages function
      const uploadTask = await setBookingUserIdImages({
        hotelId: hotelId,
        emailId: newEmail || "test@stayflexi.com",
        name: newName,
        phone: sanitizePhoneNumberForIdUpload(newPhone),
        frontData: userImages?.frontBas64Data ? userImages?.frontBas64Data : null,
        frontType: 'image/jpeg',
        backData: userImages?.backBase64Data ? userImages?.backBase64Data : null,
        backType: 'image/jpeg',
        passportImageData: userImages?.passportImageBase64 ? userImages?.passportImageBase64 : null,
        passportImageType: 'image/jpeg',
      });

      // Delete from old location by uploading empty data to overwrite
      try {
        await setBookingUserIdImages({
          hotelId: hotelId,
          emailId: oldEmail || "test@stayflexi.com",
          name: oldName,
          phone: sanitizePhoneNumberForIdUpload(oldPhone),
          frontData: null,
          frontType: 'image/jpeg',
          backData: null,
          backType: 'image/png',
          passportImageData: null,
          passportImageType: 'image/jpeg',
        });
      } catch (deleteError) {
        console.error('Error clearing old user images:', deleteError);
      }

      return uploadTask;
    }
  } catch (error) {
    console.error('Error moving user ID images:', error);
  }
};
